import { useEffect, useState, useCallback } from "react";
import { Plus, Pencil, Loader2, Building2, Package, History } from "lucide-react";
import { toast } from "sonner";
import { GlassCard } from "../../components/common/ui";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Switch } from "../../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import api, { formatApiError } from "../../lib/api";

const inp = "mt-1 bg-black/30 border-white/15 text-white";
const EMPTY = {
  name: "", trading_name: "", supplier_code: "", contact_name: "", email: "",
  telephone: "", website: "", notes: "", is_active: true, default_currency: "GBP",
  default_vat_treatment: "inc_vat", item_number_column: "", barcode_column: "", product_name_column: "",
};

export default function AdminSuppliers() {
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/admin/suppliers").then(({ data }) => setSuppliers(data)).catch(() => {}).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setForm(EMPTY); setEditingId(null); setOpen(true); };
  const openEdit = (s) => { setForm({ ...EMPTY, ...s }); setEditingId(s.supplier_id); setOpen(true); };
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.name.trim()) { toast.error("Supplier name is required"); return; }
    setSaving(true);
    try {
      if (editingId) await api.patch(`/admin/suppliers/${editingId}`, form);
      else await api.post("/admin/suppliers", form);
      toast.success(editingId ? "Supplier updated" : "Supplier created");
      setOpen(false); load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Save failed");
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-6" data-testid="admin-suppliers">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="font-playfair text-2xl sm:text-3xl text-white flex items-center gap-2"><Building2 className="h-6 w-6 text-gold" /> Suppliers</h1>
          <p className="mt-2 text-muted-foreground">Manage suppliers and their default import settings. Contact details and cost configuration are admin-only and never exposed publicly.</p>
        </div>
        <Button data-testid="add-supplier-btn" onClick={openNew} className="cf-gold-btn font-semibold gap-2"><Plus className="h-4 w-4" /> Add Supplier</Button>
      </div>

      <GlassCard className="p-0 overflow-hidden" hover={false}>
        {loading ? (
          <div className="p-8 flex items-center gap-2 text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin text-gold" /> Loading…</div>
        ) : suppliers.length === 0 ? (
          <div className="p-10 text-center text-muted-foreground">No suppliers yet.</div>
        ) : (
          <Table>
            <TableHeader><TableRow className="border-white/10 hover:bg-transparent">
              <TableHead className="text-white/70">Supplier</TableHead>
              <TableHead className="text-white/70">Code</TableHead>
              <TableHead className="text-white/70">Products</TableHead>
              <TableHead className="text-white/70">Imports</TableHead>
              <TableHead className="text-white/70">Status</TableHead>
              <TableHead className="text-white/70 text-right">Actions</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {suppliers.map((s) => (
                <TableRow key={s.supplier_id} className="border-white/10" data-testid={`supplier-row-${s.supplier_id}`}>
                  <TableCell className="text-white font-medium">{s.name}{s.trading_name && <span className="text-white/40 text-xs block">{s.trading_name}</span>}</TableCell>
                  <TableCell className="text-muted-foreground text-sm">{s.supplier_code}</TableCell>
                  <TableCell className="text-muted-foreground"><span className="inline-flex items-center gap-1"><Package className="h-3.5 w-3.5" />{s.product_count}</span></TableCell>
                  <TableCell className="text-muted-foreground"><span className="inline-flex items-center gap-1"><History className="h-3.5 w-3.5" />{s.import_count}</span></TableCell>
                  <TableCell className={s.is_active ? "text-emerald-400 text-sm" : "text-white/40 text-sm"}>{s.is_active ? "Active" : "Inactive"}</TableCell>
                  <TableCell className="text-right">
                    <button onClick={() => openEdit(s)} data-testid={`edit-supplier-${s.supplier_id}`} className="inline-grid place-items-center h-8 w-8 rounded-md text-white/60 hover:text-gold hover:bg-white/10"><Pencil className="h-4 w-4" /></button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </GlassCard>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="cf-glass border-white/10 text-white max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="supplier-dialog">
          <DialogHeader><DialogTitle className="font-playfair text-xl">{editingId ? "Edit Supplier" : "New Supplier"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <div><Label className="text-white/80">Name *</Label><Input data-testid="supplier-name-input" value={form.name} onChange={(e) => set("name", e.target.value)} className={inp} /></div>
              <div><Label className="text-white/80">Trading name</Label><Input value={form.trading_name || ""} onChange={(e) => set("trading_name", e.target.value)} className={inp} /></div>
              <div><Label className="text-white/80">Supplier code</Label><Input value={form.supplier_code || ""} onChange={(e) => set("supplier_code", e.target.value)} className={inp} /></div>
              <div><Label className="text-white/80">Contact name</Label><Input value={form.contact_name || ""} onChange={(e) => set("contact_name", e.target.value)} className={inp} /></div>
              <div><Label className="text-white/80">Email</Label><Input value={form.email || ""} onChange={(e) => set("email", e.target.value)} className={inp} /></div>
              <div><Label className="text-white/80">Telephone</Label><Input value={form.telephone || ""} onChange={(e) => set("telephone", e.target.value)} className={inp} /></div>
              <div><Label className="text-white/80">Website</Label><Input value={form.website || ""} onChange={(e) => set("website", e.target.value)} className={inp} /></div>
              <div><Label className="text-white/80">Default currency</Label><Input value={form.default_currency || ""} onChange={(e) => set("default_currency", e.target.value)} className={inp} /></div>
            </div>
            <div><Label className="text-white/80">Notes</Label><Textarea value={form.notes || ""} onChange={(e) => set("notes", e.target.value)} className={inp} /></div>
            <div className="grid sm:grid-cols-3 gap-4 border-t border-white/10 pt-4">
              <div><Label className="text-white/70 text-xs">Item-number column</Label><Input value={form.item_number_column || ""} onChange={(e) => set("item_number_column", e.target.value)} className={inp} /></div>
              <div><Label className="text-white/70 text-xs">Barcode column</Label><Input value={form.barcode_column || ""} onChange={(e) => set("barcode_column", e.target.value)} className={inp} /></div>
              <div><Label className="text-white/70 text-xs">Product-name column</Label><Input value={form.product_name_column || ""} onChange={(e) => set("product_name_column", e.target.value)} className={inp} /></div>
            </div>
            <div className="flex items-center gap-2"><Switch data-testid="supplier-active-switch" checked={form.is_active} onCheckedChange={(v) => set("is_active", v)} /><Label className="text-white/80">Active</Label></div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)} className="text-white/70">Cancel</Button>
            <Button data-testid="save-supplier-btn" onClick={save} disabled={saving} className="cf-gold-btn font-semibold gap-2">{saving && <Loader2 className="h-4 w-4 animate-spin" />} Save Supplier</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
